import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import * as FavoriteReportActions from 'features/frontoffice/favoriteReport/actions/action';
import ReportChip from 'features/frontoffice/component/reportFilter';
import { IconX } from '@tabler/icons-react';
import { SAVED_CHIP_CATEGORIES } from 'common/Constant';
import { AppConfig } from 'AppConfig';
import { hasPermission } from 'utils/common';

const toUpper = (v) => (v ? String(v).trim().toUpperCase() : '');


export const getItemCategory = (item) => toUpper(item?.category || item?.reportType);

export const getAccessibleFavoritedReports = (reports, permissions) => {
  const canViewAdHoc = hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions);
  const canViewExclusive = hasPermission(AppConfig.PERMISSION.FO_EXCLUSIVE_REPORTS, permissions);

  return Array.isArray(reports) ? reports.filter((report) => {
    if (!report?.favorited) return false;
    const category = getItemCategory(report);
    if (category === 'EXCLUSIVE' && !canViewExclusive) return false;
    if ((category === 'AD_HOC' || category.startsWith('ADHOC')) && !canViewAdHoc) return false;
    return true;
  }) : [];
};


const buildCounts = (reports) => {
  const counts = {};
  SAVED_CHIP_CATEGORIES.forEach(({ key }) => {
    counts[key] = 0;
  });
  if (!Array.isArray(reports)) return counts;

  reports.forEach((item) => {
    if(item?.dismissed) return; // Skip dismissed reports
    const cat = getItemCategory(item);
    const match = SAVED_CHIP_CATEGORIES.find(({ codes }) => codes.includes(cat));
    if (match) counts[match.key] += 1;
  });
  return counts;
};

const ReportFilters = ({ setData, onSelectionChange, isMobile, resetSelectionTrigger }) => {
  const dispatch = useDispatch();
  const favoriteReports = useSelector((state) => state.favoriteReport.favoriteReports);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showClearAll, setShowClearAll] = useState(false);

  // Granular mobile breakpoints
  const isXSmallMobile = useMediaQuery('(max-width:374px)');
  const isSmallMobile = useMediaQuery('(min-width:375px) and (max-width:424px)');
  const isMediumMobile = useMediaQuery('(min-width:425px) and (max-width:599px)');
  const isLargeMobile = useMediaQuery('(min-width:600px) and (max-width:767px)');
  const isMobileView = useMediaQuery('(max-width:767px)');

  const allFavorited = useMemo(() => {
    return getAccessibleFavoritedReports(favoriteReports, permissions);
  }, [favoriteReports, permissions]);

  const counts = useMemo(() => buildCounts(allFavorited), [allFavorited]);

  useEffect(() => {
    if (typeof setData !== 'function') return;
    if (!Array.isArray(allFavorited)) {
      setData([]);
      return;
    }
    if (selectedKeys.length === 0) {
      setData(allFavorited); // "All"
    } else {
      const activeCodes = SAVED_CHIP_CATEGORIES.filter(({ key }) => selectedKeys.includes(key)).flatMap(({ codes }) => codes);
      const filtered = allFavorited.filter((item) => activeCodes.includes(getItemCategory(item)));
      setData(filtered);
    }
  }, [selectedKeys, allFavorited, setData]);

  useEffect(() => {
    if (!isMobileView && selectedKeys.length > 0) {
      setShowClearAll(true);
    }
  }, [selectedKeys]);

  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      const selected = SAVED_CHIP_CATEGORIES.filter(({ key }) => selectedKeys.includes(key));
      onSelectionChange(selected);
    }
  }, [selectedKeys, onSelectionChange]);

  useEffect(() => {
      setSelectedKeys([]);
      setShowClearAll(false);
  }, [resetSelectionTrigger]);

  const toggleKey = (key) => setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const getChipWidth = () => {
    if (isMobileView) return '100%';
    return 'auto';
  };

  return (
    <Stack
      direction={isMobileView ? 'column' : 'row'}
      columnGap="12px"
      rowGap="12px"
      sx={{
        alignItems: isMobileView ? 'flex-start' : 'center',
        ml: 'auto',
        flexWrap: isMobileView ? 'nowrap' : 'wrap',
        width: '100%',
        minHeight: 'auto'
      }}>
      {SAVED_CHIP_CATEGORIES.map(({ key, label }) => {
        const isSelected = selectedKeys.includes(key);
        return (
          <ReportChip
            key={key}
            item={{ name: label }}
            total={counts[key]}
            selected={isSelected}
            onToggle={() => toggleKey(key)}
            isMobile={isMobileView}
            chipWidth={getChipWidth()}
          />
        );
      })}

      {showClearAll && selectedKeys.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: '#2F54A5',
            marginTop: '5px',
            '&:hover': {
              color: '#068DF1'
            }
          }}
          onClick={() => setSelectedKeys([])}
          >
          <IconX height={16} width={16} style={{ cursor: 'pointer' }} />
          <Typography
            data-testid="saved-chip-clear-all"
            sx={{
              cursor: 'pointer',
              fontFamily: 'Noto Sans',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '150%'
            }}>
            Clear All
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default ReportFilters;
